import { SchemesEnum, EnvsEnum } from '@utils/types';

import { CryptoCurrencyEnum, FiatCurrencyEnum } from '@modules/global/types';
import { AuthFlowEnum } from '@modules/types';
/* eslint-disable sonarjs/no-duplicate-string */
if (typeof window.__APP_CONFIG__ === 'undefined') {
  throw new Error('Configuration file is missing');
}

import { getNonce } from './cspNonce';

const { apiScheme, apiHost, apiVersion, env } = window.__APP_CONFIG__;
const landingDomain = env === EnvsEnum.prod ? 'https://mercuryo.io' : 'https://mrcr.io';
const { BTC, BCH, ETH, USDT, DAI, BAT, BUSD, NEAR, ADA, USDC, LTC, INJ } = CryptoCurrencyEnum;
const { EUR, USD } = FiatCurrencyEnum;

const CONST = {
  ...window.__APP_CONFIG__,
  operations: {
    buy: 'buy',
    sell: 'sell',
    withdraw: 'withdraw',
    deposit: 'deposit',
    exchange: 'fiat_buy',
    receipt: 'receipt',
    mobile_pay: 'mobile_pay',
    buy_acquirer: 'buy_acquirer',
  },
  operationsDisabled: false,
  logoutCountdownTime: 30000,
  nav: {
    root: '/',
    // Wallets
    wallets: '/wallets',
    limits: '/wallets/limits',

    // Wallets - Topup
    topup: '/wallets/topup',
    verifyCard: '/wallets/topup/verify-card',
    verifyDescriptor: '/wallets/topup/verify-descriptor',
    walletsBuyAccountsExchange: '/wallets/buy/accounts/exchange',
    walletsBuyAccountsInvoice: '/wallets/buy/accounts/invoice',
    deposit: '/wallets/deposit',
    operationWithNewCard: '/wallets/operation_with_new_card',
    operationWithNewCardVerify: '/wallets/operation_with_new_card/verify',

    // Wallets - Send
    withdraw: '/wallets/withdraw',
    walletsWithdrawAccountsExchange: '/wallets/withdraw/accounts/exchange',
    sendToBankAccount: '/send-to-bank-account',
    withdrawCrypto: '/wallets/withdraw-crypto',

    // Accounts
    accountsIntro: '/accounts/intro',
    accountsVerification: '/accounts/verification',
    accounts: '/accounts',
    accountsDetails: '/accounts/details',
    accountsBuyInvoiceDetails: '/accounts/buy-invoice-details',

    // Accounts - Topup
    accountsTopup: '/accounts/topup',
    accountsTopupWalletsExchange: '/accounts/topup/wallets/exchange',

    // Accounts - Send
    accountsSend: '/accounts/send',
    accountsSendVerification: '/accounts/send/verification',
    accountsSendWalletsExchange: '/accounts/send/wallets/exchange',

    // Invite
    invite: '/invite',

    // Profile
    profile: '/profile',
    profileOverview: '/profile/overview',
    verifyIdentity: '/profile/verify-identity',
    profileEdit: '/profile/edit',
    profileSetPhone: '/profile/phone/set',
    profileChangePhone: '/profile/phone/change',
    profileSetEmail: '/profile/email/set',
    profileOldEmail: '/profile/email/old',
    profileNewEmail: '/profile/email/new',
    profileNewEmailVerifyPassword: '/profile/password/verify',
    profileSetPassword: '/profile/password/set',
    profileChangePassword: '/profile/password/change',
    profileCardsAdd: '/profile/cards/add',
    profileCardsVerify: '/profile/cards/verify',
    profileBillingAddress: '/profile/billing-address/change',

    balances: '/balances',
    login: '/login',
    loginEmail: '/login/email',
    loginVerifyPhone: '/login/verify-phone',
    loginVerifyOTP: '/login/verify-otp',
    loginSetEmail: '/login/set-email',
    loginVerifyEmail: '/login/verify-email',
    loginVerifyPassword: '/login/verify-password',
    loginResetPasswordVerifyEmail: '/login/reset-password/verify-email',
    loginSetPassword: '/login/set-password',
    error: '/login/error',
    accountExists: '/user/exists',
    accountBlocked: '/login/account-blocked',
    kycBlocked: '/kyc/blocked',
    profileLimits: '/profile/limits',
    changeEmailVerifyPhone: '/account/change-email/verify-phone',
    changeEmailVerifyEmail: '/account/change-email/verify-email',
    changePhoneVerifyEmail: '/account/change-phone/verify-email',
    changePhoneVerifyPhone: '/account/change-phone/verify-phone',
    changePasswordVerifyPhone: '/account/change-password/verify-phone',
    changePasswordVerifyEmail: '/account/change-password/verify-email',
    verification: '/verification',
    verificationInProgress: '/verification/in-progress',
    verificationIncomplete: '/verification/incomplete',
    withdrawCryptoVerifyPhone: '/withdraw-crypto',
    cards: '/cards',
    verifyOtp: '/verify-otp',
    paymentMethod: '/payment-method',
    portfolio: '/wallet/portfolio',
    transactions: '/wallet/transactions',
    purchase: '/wallet/purchase',
    balance: '/wallets/balance',

    // 2fa
    intro2fa: '/protect',
    setup2fa: '/protect/setup',
    success2fa: '/protect/finish',
    verify2fa: '/protect/verify',
  },
  defaultScheme: SchemesEnum.dark,
  getBindStatusMaxCount: 60,
  getBindCardStatusInterval: 2000,
  KYCRedirectTimeout: 5000,
  getStatusInterval: 2000,
  KYCStatusInterval: 5000,
  keepAliveInterval: 1 * 60 * 1000,
  card3DS: {
    cardReturnParam: 'card_id',
    buyReturnParam: 'buy_id',
    statuses: {
      pending: 'pending',
      succeeded: 'succeeded',
      failed: 'failed',
      deleted: 'deleted',
    },
  },
  transactions: {
    pageSize: 20,
  },
  i18n: {
    defaultLang: 'en',
    languages: {
      en: {
        code: 'en-US',
        name: 'English',
      },
      ru: {
        code: 'ru-RU',
        name: 'Русский',
      },
      de: {
        code: 'de-DE',
        name: 'Deutsch',
      },
      ko: {
        code: 'ko-KR',
        name: '한국어',
      },
      es: {
        code: 'es-ES',
        name: 'Español',
      },
    },
  },
  event3dsStarted: 'mercuryo3dsStarted',
  event3dsPassed: 'mercuryo3dsPassed',
  urls: {
    termsUrl: `${landingDomain}/legal/terms?utm_source=my.mercuryo&utm_medium=referral`,
  },
  api: {
    url: `${apiScheme}://${apiHost}/v${apiVersion}`,
  },
  apiGo: {
    url: `${apiScheme}://${apiHost}`,
  },
  getRatesDebounceTime: 500,
  inputDebounceTime: 500,
  getLimitsInterval: 10000,
  idleInterval: 1000,
  client: 'web',
  ukCountryCode: 'gb',
  website: 'https://mercuryo.io',
  help: {
    EGLD: 'https://help.mercuryo.io/en/articles/4904264-how-to-convert-%D0%B5gld-erc-20-to-egld-native-token',
  },
  errorCodes: {
    globalError: 400000,
    keyIsInvalid: 400001,
    invalidCurrencyError: 400001,
    keepAliveOTP: 403001,
    unauthorized: 401000,
    keyExpired: 401001,
    limitsExceeded: 400005,
    cardLimitsExceeded: 400006,
    accountIsNotVerified: 403001,
    emailLimitExceeded: 429000,
    userBlocked: 403009,
    captchaCode: 400001,
    descriptorTriesDepleted: 403022,
    unableToEstimateFee: 403028,
    amountOffLimits: 400005,
    rateExpired: 403004,
    ERR_NETWORK: 'ERR_NETWORK',
  },
  defaultCountryCode: 'gb',
  mask: {
    card: {
      cvv: '999',
    },
    flutterWave: {
      pin: '99999999',
    },
    unlimint: {
      customerIdentity: '999.999.999-99',
    },
  },
  defaultFlow: AuthFlowEnum.email,
  defaultFiatCurrency: FiatCurrencyEnum.USD,
  defaultCryptoCurrency: CryptoCurrencyEnum.BTC,
  numberWalletsToShow: 5,
  accountsCurrency: FiatCurrencyEnum.EUR,
  zhCurrencies: [BTC, BCH, ETH, USDT, DAI, BAT] as CryptoCurrencyEnum[],
  sellCurrencies: [EUR, USD] as FiatCurrencyEnum[],
  popularCurrencies: [BTC, ETH, USDT, BUSD, NEAR] as CryptoCurrencyEnum[],
  validateAddressIgnoreCurrencies: [ADA, USDC, LTC, INJ] as CryptoCurrencyEnum[],
  infoLinks: [
    {
      text: 'footer.tos',
      link: '/legal/terms',
    },
    {
      text: 'footer.privacy',
      link: '/legal/privacy',
    },
    {
      text: 'footer.personal_data',
      link: '/legal/cookies',
    },
    {
      text: 'menu.help',
      link: '/help',
    },
  ],
  zendeskScript:
    'https://static.zdassets.com/ekr/snippet.js?key=2ba8c6cf-6eb8-408b-9a98-192838e14f59',
  get CSP_NONCE() {
    return getNonce();
  },
} as const;

export default CONST;
